import { 
  PointerSensor, 
  KeyboardSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';

// Tags, roles, and classes of elements that should never function as drag handles
const IGNORE_TAGS = ['BUTTON', 'INPUT', 'TEXTAREA']
const IGNORE_ROLES = ['button']
const IGNORE_CLASSES = ['modal-open', 'w-modal-mask', 'w-modal']

/**
 * Determines whether interacting with an element should initiate dragging and
 * dropping. Prevents interactions with certain children of draggable items (for
 * example, an edit button) from unintentionally initiating dragging.
 * 
 * Starting with target, steps outward through parent elements until it finds
 * one marked as draggable, or identifies one that should not be draggable.
 * - Dragging proceeds if:
 *   1) the data-dnd attribute is "true" (always the case for drag handles, and
 *      for draggable elements without a handle)
 * - Dragging is blocked if:
 *   1) the element is a button, input, or textarea
 *   2) the element is an open modal (Bootstrap or WingsModal)
 *   3) the data-dnd attribute is "false"
 *
 * @param {EventTarget} target - target of interaction event
 * @returns {boolean} whether dragging can be initiated
 */
const canElementInitiateDragging = (target) => {
  let current = target;
  while (current && !(current.dataset && current.dataset.dnd === "true")) {
    if (IGNORE_TAGS.includes(current.tagName) 
        || IGNORE_ROLES.includes(current.getAttribute('role'))
        || (current.dataset && current.dataset.dnd === "false") 
        || (current.classList && IGNORE_CLASSES.some((c) => current.classList.contains(c)))) {
      return false;
    }
    current = current.parentElement;
  }

  return true;
}

/**
 * Copy a sensor class from dnd-kit, but make two additions:
 * 1) Add check to the activator functions (see *canElementInitiateDragging* for
 *    more info)
 * 2) Capture the pointer to the dragged element or its drag handle (if it
 *    exists) to keep it from interacting with other elements (Ex: button/link
 *    hover states) while dragging
 * @param {ClassType} Sensor 
 * @returns {ClassType}
 */
const createCustomSensor = (Sensor) => {
  function CustomSensor(props) {
    return new Sensor(props)
  }

  CustomSensor.activators = (Sensor.activators || []).map(({ eventName, handler }) => ({
    eventName,
    handler(event, ...rest) {
      const canInitiate = handler(event, ...rest) && canElementInitiateDragging(event.target)
      if (canInitiate && event.pointerId != null) {
        event.currentTarget.setPointerCapture(event.pointerId)
      }
      return canInitiate
    },
  }))

  return CustomSensor
}

const CustomPointerSensor = createCustomSensor(PointerSensor);
const CustomKeyboardSensor = createCustomSensor(KeyboardSensor);

/**
 * Use custom drag-and-drop sensors that allow better control of what initiates
 * dragging and dropping. See the documentation of *canElementInitiateDragging*
 * for an in-depth description of this.
 */
const useCustomSensors = () => {
  return useSensors(
    useSensor(CustomPointerSensor),
    useSensor(CustomKeyboardSensor),
  )
}

export {
  useCustomSensors,
}
